import queryString from 'query-string';

import { ROUTE_PATHS } from '../../config/routes';

import healthMetrics from '../../utils/health-metrics/health-metrics';
import Logger from '../../utils/logger/logger';

import analytics from '../../../shared/analytics';
import ClickTracker from '../../../shared/click-tracker/click-tracker';
import UiElement from '../../../shared/ui-element/ui-element';

import EmailSubscription from '../email-subscription/email-subscription';
import StoreEmailSubscription from '../store-email-subscription-base/store-email-subscription-base';
import StoreLangSwitcher from '../store-lang-switcher/store-lang-switcher';
import Navigation from '../navigation/navigation';
import StoreNavigation from '../store-navigation/store-navigation';
import StoreSidebar from '../store-sidebar/store-sidebar';
import StoreSearch from '../store-search/store-search';
import Footer from '../footer/footer';
import SignupModal from '../signup-modal/signup-modal';
import CookieBanner from '../cookie-banner/cookie-banner';
import EmergencyMessage from '../emergency-message/emergency-message';
import RedirectTo from '../redirect-to/redirect-to';
import CaptchaProvider from '../../../shared/captcha-provider/captcha-provider';
import BlogSubscribeBanner from '../blog-subscribe-banner/blog-subscribe-banner';
import EneyPromoBanner from '../eney-promo-banner/eney-promo-banner';

import '../../main.scss';


class BasePage extends UiElement {
  webStoreEnabled = false;
  eneyPromoBannerEnabled = false;

  signupModalOptions = {
    hideCloseBtn: false,
  };

  constructor() {
    super();

    this.ui = {
      subscribeBanner: 'js-subscribe-banner',
    };

    this.captchaProvider = new CaptchaProvider();
    this.signupModal = new SignupModal(this.captchaProvider);
    this.analytics = analytics;
  }

  onInit() {
    Logger.initialize();

    this.captchaProvider.init();

    this.webStoreEnabled = window.ABN_TESTS_DATA?.feature_flags.some((flag) => flag?.key === 'isNewWebStore' && flag?.value);
    this.eneyPromoBannerEnabled = window.ABN_TESTS_DATA?.feature_flags.some((flag) => flag?.key === 'eneyPromoBanner' && flag?.value);

    if (this.webStoreEnabled) {
      new StoreNavigation({ rootElement: '.js-store-navigation' }).init();
      new StoreSidebar({ rootElement: '.js-store-sidebar' }).init();
      new StoreSearch({ rootElement: '.js-store-search' }).init();
      new StoreSearch({ rootElement: '.js-store-sidebar-search' }).init();

      const storeSubscriptionForm = document.querySelector('.js-store-subscription-form');
      if (storeSubscriptionForm) {
        new StoreEmailSubscription({ rootElement: storeSubscriptionForm }).init();
      }

      const storeLangSwitcher = document.querySelector('.js-store-lang-switcher');
      if (storeLangSwitcher) {
        new StoreLangSwitcher({ rootElement: storeLangSwitcher }).init();
      }
    } else {
      new EmailSubscription({ rootElement: '.js-base-subscription-form' }, this.captchaProvider).init();

      new Navigation().init();
    }

    if (this.ui.subscribeBanner) {
      new BlogSubscribeBanner({ rootElement: '.js-subscribe-banner' }, this.captchaProvider).init();
    }

    if (this.eneyPromoBannerEnabled) {
      new EneyPromoBanner().init();
    }

    new ClickTracker().init();
    new CookieBanner().init();
    new EmergencyMessage().init();
    new RedirectTo().init();
    new Footer().init();
    healthMetrics.initialize();

    this.signupModal.init();
    this.setSignupModalOptions();
    this.initSignUpButtons();
    this.openSignupModalFromExternalSourceIfNeeded();
    this.trackPageView();
  }

  getJoinMembershipUrl({ tierType, signupHideSocials, eventLabel, campaign } = {}) {
    const { joinMembership } = ROUTE_PATHS.newWebsitePages;
    const searchParams = new URLSearchParams(window.location.search);

    const campaignParam = searchParams.get('campaign');

    if (tierType) searchParams.set('tierType', tierType);
    if (signupHideSocials) searchParams.set('signupHideSocials', signupHideSocials);
    if (eventLabel) searchParams.set('eventLabel', eventLabel);
    if (campaign && !campaignParam) searchParams.set('campaign', campaign);

    const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    return `${joinMembership}${searchString}`;
  }

  setSignupModalOptions() {
    const query = queryString.parse(window.location.search);

    if ('hideModalCloseBtn' in query) {
      this.signupModalOptions = { ...this.signupModalOptions, hideCloseBtn: true };
    }
  }

  trackPageView() {
    healthMetrics.trackIncrement({ name: 'page_view' });
  }

  initSignUpButtons() {
    const websiteCommonSignupButtons = document.querySelectorAll('[data-signup-button]');
    const blogSliderSignupButtons = document.querySelectorAll('.js-slider-banner-cta');
    const blogPromoBannerSignupButtons = document.querySelectorAll('.js-promo-banner-cta');
    const blogSideScrollingSignupButtons = document.querySelectorAll('.js-side-scrolling-cta');

    const buttons = [
      ...websiteCommonSignupButtons,
      ...blogSliderSignupButtons,
      ...blogPromoBannerSignupButtons,
      ...blogSideScrollingSignupButtons,
    ];

    [].forEach.call(buttons, (button) => {
      if (this.webStoreEnabled) {
        this.signupButtonToLink(button);

        return;
      }
      button.addEventListener('click', this.handleSignUpButtonClick);
    });
  }

  signupButtonToLink(button) {
    const { tierType, signupHideSocials, eventLabel, campaign, ...restDataAttributes } = button.dataset;

    const link = document.createElement('a');

    const classNames = [...button.classList].filter((className) => !className.includes('js-'));

    link.href = this.getJoinMembershipUrl({ tierType, signupHideSocials, eventLabel, campaign });
    link.innerHTML = button.innerHTML;
    link.classList.add(...classNames);

    if (restDataAttributes) {
      Object.entries(restDataAttributes).forEach(([key, value]) => {
        link.setAttribute(key, value);
      });
    }

    button.replaceWith(link);
  }

  handleSignUpButtonClick = (e) => {
    const { tierType, signupHideSocials, eventLabel } = e.currentTarget.dataset;

    this.signupModal.open({
      ...this.signupModalOptions, tierType, signupHideSocials, eventLabel,
    });
  };

  // This is a function to open Signup modal for
  // those users who came from external landings with additional get params
  openSignupModalFromExternalSourceIfNeeded() {
    const query = queryString.parse(window.location.search);
    const externalParamsInUrl = ['appId', 'affiliates'];

    externalParamsInUrl.some((param) => {
      if (param in query) {
        this.signupModal.open(this.signupModalOptions);

        return true;
      }

      return false;
    });
  }
}

export default BasePage;
